import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { zip } from 'ramda';
import type { PredefinedFilter } from '@qre-mailing/smart-table';
import { useMutation } from '@tanstack/react-query';

import type { PostSurveyByQreIdDefaultFiltersData } from '../api/generated-evs';
import { postSurveyByQreIdDefaultFiltersMutation } from '../api/generated-evs/@tanstack/react-query.gen';

type DefaultFilterItem = NonNullable<PostSurveyByQreIdDefaultFiltersData['body']>[number] & {
  description: ReactNode;
};

export type UsePredefinedFiltersResult = {
  predefinedFilters: PredefinedFilter[];
  predefinedFiltersLoading: boolean;
  reloadPredefinedFilters: () => void;
};

const defaultFilters: DefaultFilterItem[] = [
  {
    name: 'Vše',
    filter: {},
    description: <FormattedMessage id="filter.predefinedFilters.all" defaultMessage="Všechny záznamy v sample table" />,
  },
  {
    name: 'Úspěšné',
    description: (
      <FormattedMessage
        id="filter.predefinedFilters.completedSurveys"
        defaultMessage="Úspěšně dokončené dotazníky (RC=18)"
      />
    ),
    filter: {
      andConditions: [
        {
          orConditions: [
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 18,
            },
          ],
        },
      ],
    },
  },
  {
    name: 'Odložené',
    description: (
      <FormattedMessage id="filter.predefinedFilters.postponedCalls" defaultMessage="Odložené hovory (RC = 6,7)" />
    ),
    filter: {
      andConditions: [
        {
          orConditions: [
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 6,
            },
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 7,
            },
          ],
        },
        {
          orConditions: [
            {
              field: 'parking',
              operator: 'NOT_EQUALS',
              value: 1,
            },
          ],
        },
      ],
    },
  },
  {
    name: 'Probíhající',
    description: (
      <FormattedMessage
        id="filter.predefinedFilters.inProgressCalls"
        defaultMessage="Právě probíhající hovory, kdy je tazatel v dotazníku"
      />
    ),
    filter: {
      andConditions: [
        {
          orConditions: [
            {
              field: 'Status',
              operator: 'EQUALS',
              value: 'In call (LB)',
            },
          ],
        },
      ],
    },
  },
  {
    name: 'Nepoužité',
    description: (
      <FormattedMessage id="filter.predefinedFilters.unusedContacts" defaultMessage="Nepoužité kontakty, čistá dtb" />
    ),
    filter: {
      andConditions: [
        {
          // never-called rows keep number_of_contacts NULL – the column has no DB default
          orConditions: [
            {
              field: 'NumberOfContacts',
              operator: 'EQUALS',
              value: 0,
            },
            {
              field: 'NumberOfContacts',
              operator: 'EMPTY',
            },
          ],
        },
        {
          orConditions: [
            {
              field: 'parking',
              operator: 'NOT_EQUALS',
              value: 1,
            },
          ],
        },
        {
          orConditions: [
            {
              field: 'Status',
              operator: 'NOT_EQUALS',
              value: 'Blacklisted',
            },
          ],
        },
        {
          orConditions: [
            {
              field: 'Status',
              operator: 'NOT_EQUALS',
              value: 'Eliminated',
            },
          ],
        },
      ],
    },
  },
  {
    name: 'Parkoviště',
    description: (
      <FormattedMessage
        id="filter.predefinedFilters.parking"
        defaultMessage="kontakty mimo volání (Status 'Parking=1')"
      />
    ),
    filter: {
      andConditions: [
        {
          orConditions: [
            {
              field: 'parking',
              operator: 'EQUALS',
              value: 1,
            },
          ],
        },
        {
          orConditions: [
            {
              field: 'Status',
              operator: 'NOT_EQUALS',
              value: 'Blacklisted',
            },
          ],
        },
      ],
    },
  },
  {
    name: 'Neúspěšné',
    description: (
      <FormattedMessage
        id="filter.predefinedFilters.final"
        defaultMessage="Ukončená neúspěšná volání, již se nebude volat"
      />
    ),
    filter: {
      andConditions: [
        {
          orConditions: [
            {
              field: 'Status',
              operator: 'EQUALS',
              value: 'Final',
            },
          ],
        },
        {
          orConditions: [
            {
              field: 'ResponseCode',
              operator: 'NOT_EQUALS',
              value: 18,
            },
          ],
        },
      ],
    },
  },
  {
    name: 'Nevyčerpané',
    description: (
      <FormattedMessage
        id="filter.predefinedFilters.unexhaustedAttempts"
        defaultMessage="Aktivní provolané pokusy, ještě se bude volat"
      />
    ),
    filter: {
      andConditions: [
        {
          orConditions: [
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 2,
            },
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 3,
            },
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 4,
            },
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 10,
            },
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 20,
            },
          ],
        },
        {
          orConditions: [
            {
              field: 'Status',
              operator: 'NOT_EQUALS',
              value: 'Final',
            },
          ],
        },
        {
          orConditions: [
            {
              field: 'parking',
              operator: 'NOT_EQUALS',
              value: 1,
            },
          ],
        },
      ],
    },
  },
  {
    name: 'Vyčerpané',
    description: (
      <FormattedMessage
        id="filter.predefinedFilters.exhaustedAttempts"
        defaultMessage="Vyčerpané pokusy, bylo již dosaženo maximálního počtu hovorů"
      />
    ),
    filter: {
      andConditions: [
        {
          orConditions: [
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 2,
            },
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 3,
            },
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 4,
            },
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 10,
            },
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 20,
            },
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 21,
            },
          ],
        },
        {
          orConditions: [
            {
              field: 'Status',
              operator: 'EQUALS',
              value: 'Final',
            },
            {
              field: 'Status',
              operator: 'EQUALS',
              value: 'Eliminated',
            },
          ],
        },
        {
          orConditions: [
            {
              field: 'parking',
              operator: 'NOT_EQUALS',
              value: 1,
            },
          ],
        },
      ],
    },
  },
  {
    name: 'Duplicity',
    description: (
      <FormattedMessage
        id="filter.predefinedFilters.duplicityPhoneNumbers"
        defaultMessage="Řádky se duplicitními telefonními čísly"
      />
    ),
    filter: {
      andConditions: [
        {
          orConditions: [{ field: 'phones*', operator: 'DUPLICITY' }],
        },
      ],
    },
  },
  {
    name: 'Blacklist',
    description: (
      <FormattedMessage
        id="filter.predefinedFilters.blacklist"
        defaultMessage="Čísla na blacklistu (ResponseCode 60/70)"
      />
    ),
    filter: {
      andConditions: [
        {
          orConditions: [
            {
              // may be removed in the future
              field: 'TelephoneNumberStatus',
              operator: 'EQUALS',
              value: 'blacklisted',
            },
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 60,
            },
            {
              field: 'ResponseCode',
              operator: 'EQUALS',
              value: 70,
            },
          ],
        },
      ],
    },
  },
  {
    name: 'Chybné',
    description: <FormattedMessage id="filter.predefinedFilters.incorrect" defaultMessage="Tazatel nezadal RC" />,
    filter: {
      andConditions: [
        {
          orConditions: [
            {
              field: 'SystemData',
              operator: 'CONTAINS',
              value: 'ERR',
            },
          ],
        },
      ],
    },
  },
  {
    name: 'System Error',
    description: (
      <FormattedMessage
        id="filter.predefinedFilters.systemError"
        defaultMessage="Systémový error, ústředna nedokázala rozpoznat hlášku od operátora"
      />
    ),
    filter: {
      andConditions: [
        {
          orConditions: [
            {
              field: 'SystemData',
              operator: 'CONTAINS',
              value: '99',
            },
          ],
        },
      ],
    },
  },
];

export function usePredefinedFilters(qreId: string): UsePredefinedFiltersResult {
  const [predefinedFilters, setPredefinedFilters] = useState<PredefinedFilter[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  const { mutateAsync } = useMutation(postSurveyByQreIdDefaultFiltersMutation());
  const mutateAsyncRef = useRef(mutateAsync);
  mutateAsyncRef.current = mutateAsync;

  const fetchPredefinedFilters = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await mutateAsyncRef.current({
        path: { qreId },
        body: defaultFilters.map(({ name, filter }) => ({ name, filter })),
      });
      const result = zip(defaultFilters, response).map(([filter, resp]) => ({
        name: filter!.name,
        description: filter!.description,
        sum: resp.sum,
        filter: filter!.filter,
      })) as PredefinedFilter[];
      setPredefinedFilters(result);
    } catch (error) {
      console.error('Error fetching predefined filters:', error);
    } finally {
      setIsLoading(false);
    }
  }, [qreId]);

  useEffect(() => {
    fetchPredefinedFilters();
  }, [fetchPredefinedFilters, reloadTrigger]);

  const reload = useCallback(() => {
    setReloadTrigger((prev) => prev + 1);
  }, []);

  return {
    predefinedFilters,
    predefinedFiltersLoading: isLoading,
    reloadPredefinedFilters: reload,
  };
}
